import React, {
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react"
import { throttle } from "lodash"
import { useDrawerContext } from "./DrawerContext"
import { usePrevious } from "../utils/usePrevious"
import { MOBILE_MEDIA_QUERY, isSmallViewport } from "../constants"

export const useElementSize = () => {
  const elementRef = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const throttledSetSize = throttle((width, height) => {
      setSize({ width, height })
    }, 200)
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        throttledSetSize(width, height)
      }
    })
    const node = elementRef.current
    if (node) {
      observer.observe(node)
    }
    return () => {
      if (node) {
        observer.unobserve(node)
      }
    }
  }, [])
  return {
    elementRef,
    size,
  }
}

// tracks the same media query the CSS uses, so it is correct on the
// very first render (no width-0 flash) and never disagrees with CSS
const useIsSmall = () => {
  const [isSmall, setIsSmall] = useState(isSmallViewport)
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
    const onChange = (e) => setIsSmall(e.matches)
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [])
  return isSmall
}

const AppSizeContext = React.createContext(null)

export const AppSizeContextProvider = ({ children }) => {
  const { elementRef, size } = useElementSize()
  const isSmall = useIsSmall()
  const { setOpen } = useDrawerContext()

  const prevSmall = usePrevious(isSmall)
  const openDrawer = useCallback(() => setOpen(true), [setOpen])

  // growing past the breakpoint turns the drawer from an overlay into
  // a fixed panel — make sure it's visible
  useEffect(() => {
    if (prevSmall && !isSmall) {
      openDrawer()
    }
  }, [isSmall, prevSmall, openDrawer])

  return (
    <AppSizeContext.Provider
      value={{
        isSmall,
        elementRef,
        size,
      }}
    >
      {children}
    </AppSizeContext.Provider>
  )
}

export const useAppSizeContext = () => {
  const value = useContext(AppSizeContext)
  if (!value) {
    throw new Error("called useAppSizeContext() outside of provider")
  }
  return value
}
